import styled, { css } from 'styled-components';
import MEDIA_QUERIES from '../../mediaQueries';

export const PADDING = 4;
export const GADGET_HEADERS_BACKGROUND_COLOR = 'var(--color-grid-border)';
export const HEADERS_BACKGROUND_COLOR = 'var(--color-bg-primary)';
export const HEADERS_BORDER_COLOR = 'var(--color-border-secondary)';
export const HEADERS_BORDER_BOTTOM_COLOR = 'var(--color-grid-border-alt)';
export const COL_BORDER_COLOR = 'var(--color-border-secondary)';
// Applied to the matrix "comparison value" column header (ENG-773) so it reads
// as a distinct value column separated from the delta columns.
export const MATRIX_COMPARISON_VALUE_HEADER_CLASS =
  'matrixComparisonValueHeader';
// Applied to reversed-axis matrix cells that must render bold (the metric-name
// column and the pinned Total column). We can't rely on `cellStyle` font-weight
// on the `.ag-cell`: the inner content elements get their own weight, and a
// value set directly on the child beats an inherited one (even `!important` on
// the ancestor). So the rule targets the cell's descendants directly.
export const MATRIX_BOLD_CELL_CLASS = 'matrixBoldCell';
export const ODD_ROW_COLOR = 'var(--color-grid-odd-row)';
export const ODD_ROW_HOVER = 'var(--color-grid-odd-row-hover)';
export const EVEN_ROW_HOVER = 'var(--color-grid-even-row-hover)';
export const GRID_BORDERS_CSS = css`
  border: 1px solid ${HEADERS_BORDER_COLOR} !important;
`;
export const GRID_DARK_HEADER_CSS = css`
  .ag-theme-fleetops {
    --ag-header-background-color: ${GADGET_HEADERS_BACKGROUND_COLOR} !important;
  }
`;
export const ROW_HEIGHTS = {
  XXS: 20,
  XS: 24,
  S: 26,
  M: 26,
  L: 28,
};

export const HEADER_SPAN_CSS = css`
  font-family: Inter;
  font-weight: 550;
  letter-spacing: 0em;
  color: var(--color-text-primary);

  @media ${MEDIA_QUERIES.DESKTOP_XXS} {
    font-size: 8px;
    --ag-icon-size: 8px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_XS} {
    font-size: 8px;
    --ag-icon-size: 8px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_S} {
    font-size: 10px;
    --ag-icon-size: 10px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_M} {
    font-size: 11px;
    --ag-icon-size: 11px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_L} {
    font-size: 11px;
    --ag-icon-size: 12px;
    line-height: normal;
  }
`;
export const SPAN_CSS = css`
  font-family: Inter;
  font-weight: 400;
  letter-spacing: 0em;
  color: var(--color-text-primary);

  @media ${MEDIA_QUERIES.DESKTOP_XXS} {
    font-size: 8px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_XS} {
    font-size: 9px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_S} {
    font-size: 10px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_M} {
    font-size: 11px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_L} {
    font-size: 11px;
    line-height: normal;
  }
`;

export const SEARCH_HEADER_SPAN_CSS = css`
  font-family: Inter;
  font-weight: 550;
  letter-spacing: 0em;
  color: var(--color-text-primary);

  @media ${MEDIA_QUERIES.DESKTOP_XXS} {
    font-size: 10px;
    --ag-icon-size: 10px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_XS} {
    font-size: 10px;
    --ag-icon-size: 10px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_S} {
    font-size: 11px;
    --ag-icon-size: 11px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_M} {
    font-size: 13px;
    --ag-icon-size: 14px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_L} {
    font-size: 13px;
    --ag-icon-size: 14px;
    line-height: normal;
  }
`;
export const SEARCH_SPAN_CSS = css`
  font-family: Inter;
  font-weight: 400;
  letter-spacing: 0em;
  color: var(--color-text-primary);

  @media ${MEDIA_QUERIES.DESKTOP_XXS} {
    font-size: 10px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_XS} {
    font-size: 11px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_S} {
    font-size: 12px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_M} {
    font-size: 13px;
    line-height: normal;
  }
  @media ${MEDIA_QUERIES.DESKTOP_L} {
    font-size: 13px;
    line-height: normal;
  }
`;

const StyleOverrides = styled.div<{
  clickable?: boolean;
  isPerformanceSettings?: boolean;
}>`
  .ag-theme-fleetops {
    --ag-header-background-color: ${HEADERS_BACKGROUND_COLOR};
    --ag-header-foreground-color: var(--color-grid-header-text);

    --ag-background-color: var(--color-bg-primary);
    --ag-odd-row-background-color: ${ODD_ROW_COLOR};
    --ag-foreground-color: var(--color-grid-cell-text);
    --ag-secondary-foreground-color: var(--color-grid-header-text);
    --ag-borders: 1px solid;
    --ag-borders-critical: 1px solid;
    --ag-borders-secondary: 1px solid;
    --ag-border-color: var(--color-grid-border-row);
    --ag-secondary-border-color: var(--color-grid-border-row);
    --ag-row-border-style: solid;
    --ag-row-border-width: 1px;
    --ag-row-border-color: var(--color-grid-border-row);
    --ag-selected-row-background-color: var(--color-grid-selected-row);
    --ag-row-hover-color: ${EVEN_ROW_HOVER};
    --ag-cell-horizontal-padding: ${PADDING}px;
    --ag-control-panel-background-color: var(--color-bg-primary);

    html[data-theme='dark'] & {
      --ag-modal-overlay-background-color: rgba(0, 0, 0, 0.5);
    }

    --ag-header-column-separator-display: none;

    @media ${MEDIA_QUERIES.DESKTOP_XXS} {
      --ag-row-height: 22px;
      --ag-header-height: 22px;
    }
    @media ${MEDIA_QUERIES.DESKTOP_XS} {
      --ag-row-height: 26px;
      --ag-header-height: 26px;
    }
    @media ${MEDIA_QUERIES.DESKTOP_S} {
      --ag-row-height: 26px;
      --ag-header-height: 26px;
    }
    @media ${MEDIA_QUERIES.DESKTOP_M} {
      --ag-row-height: 28px;
      --ag-header-height: 28px;
    }
    @media ${MEDIA_QUERIES.DESKTOP_L} {
      --ag-row-height: 31px;
      --ag-header-height: 31px;
    }
  }

  /*
   * Firefox ignores the webkit ::-webkit-scrollbar styling in global-styles, so
   * ag-grid's vertical scrollbar renders at the native ~15px width and sits over
   * the last column, hiding its right-aligned values (the last cell extends to
   * the viewport edge and the value has only PADDING*2 of right padding). Rather
   * than touch the scrollbar layout, simply inset the last column's content
   * further than a native scrollbar is wide, so values always clear it. Firefox
   * only — webkit keeps its 4px bar and the standard padding. ENG-912.
   */
  @supports (-moz-transform: translate(0, 0)) {
    .ag-body-viewport {
      scrollbar-width: thin !important;
    }
    .ag-cell:last-of-type,
    .ag-header-cell:last-of-type,
    .ag-header-group-cell:last-of-type {
      padding-right: 20px !important;
    }
  }

  .ag-header-icon {
    margin-left: 0 !important;
    min-width: 8px !important;
  }

  .ag-root-wrapper {
    border: none;
  }

  .ag-cell-label-container {
    padding: 0px;
  }

  .ag-floating-bottom-container {
    .ag-row {
      border-bottom: none;
    }
  }

  .ag-header-cell-comp-wrapper {
    height: 100%;
  }

  /*
   * autoHeaderHeight measures this wrapper to size the header. On v35 forcing it
   * to 100% of the (auto-sized) header height creates an infinite feedback loop
   * (measure → grow → measure). Let it size to its content on auto-height
   * headers so the measurement settles — but floor it at the standard header
   * height (a fixed value, so no circular dependency) so single-line headers
   * don't collapse below the normal height. Fixed-height headers keep 100%
   * (needed for vertical centering).
   */
  .ag-header-cell-auto-height .ag-header-cell-comp-wrapper {
    height: auto;
    min-height: var(--ag-header-height);
    /* Centre the label within the floored height (prod centres via height:100%,
       which we can't use here — it re-triggers the measurement loop). */
    display: flex;
    align-items: center;
  }

  .ag-body-vertical-scroll {
    position: absolute;
    right: 0px;
  }

  .ag-header-cell:first-of-type {
    padding-left: ${PADDING * 2}px;
  }

  .ag-cell {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 100%;
  }

  .right-aligned-cell {
    justify-content: flex-end;
  }

  .ag-cell:first-of-type {
    padding-left: ${PADDING * 2}px;
  }

  .ag-header-cell:last-of-type {
    padding-right: ${PADDING * 2}px;
  }

  .ag-cell:last-of-type {
    padding-right: ${PADDING * 2}px;
  }

  .ag-row-odd.ag-row-hover::before {
    background-color: ${ODD_ROW_HOVER} !important;
  }

  .ag-react-container {
    width: 100%;
  }

  .ag-root {
    border: none !important;
  }

  .ag-header-cell-label {
    span {
      line-height: unset;
    }
  }

  .ag-header-group-cell:not(:last-of-type) {
    border-right: 1px solid ${HEADERS_BORDER_COLOR};
  }

  .ag-header {
    border-color: ${HEADERS_BORDER_COLOR} !important;
    border-bottom-color: ${HEADERS_BORDER_BOTTOM_COLOR} !important;
  }

  .ag-header-cell {
    border-color: ${HEADERS_BORDER_COLOR} !important;
  }

  .ag-header-group-cell {
    span {
      &:not(.ag-icon) {
        ${HEADER_SPAN_CSS}
      }
    }
  }

  .ag-row-pinned {
    span {
      &:not(.ag-icon) {
        ${HEADER_SPAN_CSS}
      }
    }
  }
  .ag-header-cell {
    &:not(:last-of-type) {
      border-right: 1px solid;
    }
    span {
      &:not(.ag-icon) {
        ${HEADER_SPAN_CSS}
      }
    }
  }

  .ag-cell {
    &:last-of-type {
      border-right: none;
    }

    span {
      ${SPAN_CSS}
    }
  }

  .ag-numeric-cell {
    justify-content: flex-end;
  }

  .ag-center-cols-container,
  .ag-header-container,
  .ag-header-row,
  .ag-floating-bottom-container {
    min-width: 100% !important;
  }

  .ag-popup {
    z-index: 90001 !important;
  }

  .ag-popup-editor {
    z-index: 90001 !important;
  }

  .ag-row {
    cursor: ${(props) => (props.clickable ? 'pointer' : 'auto')};

    &:first-of-type {
      padding-left: 20px;
    }
  }

  .ag-row-show-on-hover {
    display: none;
  }

  .ag-row-hover {
    .ag-row-show-on-hover {
      display: block;
    }
  }

  .ag-row-selected {
    background-color: var(--color-bg-selected) !important;
  }

  .performance-board-row-selected {
    background-color: var(--color-bg-selected) !important;
  }

  .ag-row-group-expanded {
    background-color: var(--color-bg-tertiary) !important;
  }

  .ag-row-group-contracted {
    background-color: var(--color-bg-tertiary) !important;
  }

  .ag-header-cell:not(:first-child) {
    &:hover {
      border-right: 1px solid var(--color-logan);
      border-left: 1px solid var(--color-logan);
    }
  }

  .ag-header-cell:nth-child(2) {
    &:hover {
      border-left: 0px;
    }
  }

  // ag-grid ignores the height of global scrollbar css.
  // See global-styles.js ::-webkit-scrollbar
  .ag-body-horizontal-scroll,
  .ag-body-horizontal-scroll-viewport,
  .ag-body-horizontal-scroll-container {
    height: 9px !important;
    max-height: 9px !important;
    min-height: 9px !important;
  }

  .ag-center-cols-viewport {
    overscroll-behavior-x: contain !important;

    ::-webkit-scrollbar {
      display: none;
    }
  }

  .ag-side-bar {
    border: 0px solid !important;
    background-color: var(--color-bg-primary) !important;
  }

  .ag-header-group-cell {
    justify-content: center;
  }

  .ag-header-group-cell-label {
    justify-content: center;
  }

  .ag-floating-bottom-container {
    font-weight: bold;
    //border-top: 1px solid #e6e6ea !important;
    color: var(--color-brand-dark) !important;

    .ag-row {
      background-color: var(--color-bg-primary) !important;
    }

    .ag-row-hover {
      background-color: var(--color-bg-primary) !important;
    }
  }

  .${MATRIX_BOLD_CELL_CLASS}, .${MATRIX_BOLD_CELL_CLASS} * {
    font-weight: bold !important;
  }

  .ag-status-bar {
    padding-left: 0px !important;
  }

  .rightAlignedHeader {
    .ag-header-cell-label {
      justify-content: flex-end;
    }

    text-align: right;
  }

  // ENG-773 matrix "comparison value" column. A right border on its header so
  // the previous-period value reads as its own column, separated from the delta
  // columns to its right (the current value to its left already borders it).
  // Header-only, matching the grid's convention of no vertical body borders.
  .matrixComparisonValueHeader {
    border-right: 1px solid ${HEADERS_BORDER_COLOR} !important;
  }

  .ag-watermark {
    display: none !important;
  }

  .ag-row-group.ag-row-odd {
    background-color: var(--color-grid-odd-row) !important;
  }

  .ag-row-group.ag-row-even {
    background-color: var(--color-bg-primary) !important;
  }

  .ag-pinned-left-cols-container {
    ${(props) =>
      props.isPerformanceSettings &&
      'border-right: 1px solid var(--color-border-tertiary) !important;'}
  }

  .ag-pinned-right-cols-container {
    ${(props) =>
      props.isPerformanceSettings &&
      'border-left: 1px solid var(--color-border-tertiary) !important;'}
  }

  .ag-theme-balham .ag-header-cell::after,
  .ag-theme-balham .ag-header-group-cell::after,
  .ag-header-cell-resize::after {
    background-color: transparent !important;
  }

  // Removes a white gap above the ProjectedPayMarker
  .ag-cell-value {
    overflow: visible;
  }

  .ag-cell-focus {
    &:focus-within {
      // Removes a white gap above the ProjectedPayMarker
      overflow: hidden;
    }
  }

  .ag-theme-fleetops-search {
    .ag-header-group-cell {
      span {
        &:not(.ag-icon) {
          ${SEARCH_HEADER_SPAN_CSS}
        }
      }
    }

    .ag-row-pinned {
      span {
        &:not(.ag-icon) {
          ${SEARCH_HEADER_SPAN_CSS}
        }
      }
    }
    .ag-header-cell {
      &:not(:last-of-type) {
        border-right: 1px solid;
      }
      span {
        &:not(.ag-icon) {
          ${SEARCH_HEADER_SPAN_CSS}
        }
      }
    }

    .ag-cell {
      &:last-of-type {
        border-right: none;
      }

      span {
        ${SEARCH_SPAN_CSS}
      }
    }
  }
`;

export default StyleOverrides;
